import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useEditorConfig } from "@/components/ContentArea/editor-config-context";
import { createSnippetKey } from "@/components/ContentArea/snippet-override-store";
import { useSnippetOverrideStore } from "@/components/Playground/playground-store";
import { useUserToken } from "@/context/auth-context";
import { usePrompt } from "@/queries";
import { CompletionTemplate } from "@/types";
import { getTemplateFormat } from "@/utils/utils";

const useSnippetContent = (promptName: string, version?: number, label?: string) => {
  const { workspaceId } = useParams();
  const userToken = useUserToken();
  const config = useEditorConfig();
  const { data: prompt, isLoading } = usePrompt(userToken, {
    prompt_name: promptName,
    number: version || undefined,
    label: label,
    workspace_id: Number(workspaceId),
  });

  const format = prompt && getTemplateFormat(prompt?.prompt_template);

  // Use normalized key that excludes version and label for unified overrides
  const snippetKey = useMemo(() => createSnippetKey(promptName), [promptName]);

  const text = useMemo(() => {
    const content = (prompt?.prompt_template as CompletionTemplate)?.content?.[0];
    if (content && "text" in content) {
      return content.text as string;
    }
    return undefined;
  }, [prompt]);

  // All versions/labels of the same prompt share the same override
  let overrideContent = config?.snippetOverrides?.[snippetKey];

  const snippetOverrideStore = useSnippetOverrideStore();
  if (snippetOverrideStore) {
    overrideContent = snippetOverrideStore.snippetOverrides?.[snippetKey];
  }

  const href =
    `/workspace/${workspaceId}/prompt/${prompt?.prompt_id}/` +
    (version ? `version/${version}` : "") +
    (label ? `?label=${label}` : "");

  const promptId = prompt?.prompt_id;

  return [text, isLoading, overrideContent, format, href, promptId] as const;
};

export default useSnippetContent;
